import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

const ACCESS_TOKEN_KEY = 'edutrip.accessToken';
const REFRESH_TOKEN_KEY = 'edutrip.refreshToken';

/**
 * SSR-safe persistence for auth tokens. Backed by `localStorage` on the browser
 * and an in-memory value on the server. The access token is exposed as a signal
 * so guards/components can react to sign-in and sign-out.
 */
@Injectable({ providedIn: 'root' })
export class TokenStore {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly _accessToken = signal<string | null>(this.read(ACCESS_TOKEN_KEY));
  readonly accessToken = this._accessToken.asReadonly();

  get refreshToken(): string | null {
    return this.read(REFRESH_TOKEN_KEY);
  }

  set(accessToken: string, refreshToken?: string): void {
    this._accessToken.set(accessToken);
    this.write(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken !== undefined) this.write(REFRESH_TOKEN_KEY, refreshToken);
  }

  clear(): void {
    this._accessToken.set(null);
    this.write(ACCESS_TOKEN_KEY, null);
    this.write(REFRESH_TOKEN_KEY, null);
  }

  private read(key: string): string | null {
    if (!this.isBrowser) return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private write(key: string, value: string | null): void {
    if (!this.isBrowser) return;
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, value);
    } catch {
      /* storage unavailable (private mode, quota) — tokens stay in-memory */
    }
  }
}
